// API client utility for making authenticated requests to the dashboard API
export class APIError extends Error {
  constructor(message: string, public status: number, public data?: any) {
    super(message);
    this.name = "APIError";
  }
}

class APIClient {
  private baseURL: string;
  private onTokenExpired?: () => void;

  constructor() {
    this.baseURL = typeof window !== "undefined" ? window.location.origin : "";
  }

  // Allow setting a callback for token expiration
  setTokenExpirationHandler(handler: () => void) {
    this.onTokenExpired = handler;
  }

  private getAuthToken(): string | null {
    if (typeof window === "undefined") return null;
    return localStorage.getItem("medipups_auth_token");
  }

  private isTokenExpired(token: string): boolean {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );

      const payload = JSON.parse(jsonPayload);

      // Check if token is expired (with 30 second buffer)
      if (payload.exp && payload.exp < Date.now() / 1000 + 30) {
        return true;
      }

      return false;
    } catch (error) {
      console.error("Error checking token expiration:", error);
      return true; // Assume expired if we can't decode
    }
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = this.getAuthToken();

    // Check if token is expired before making request
    if (token && this.isTokenExpired(token)) {
      console.warn("Token is expired, triggering logout");
      if (this.onTokenExpired) {
        this.onTokenExpired();
      }
      throw new APIError("Session expired. Please log in again.", 401);
    }

    const config: RequestInit = {
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    const response = await fetch(`${this.baseURL}/api${endpoint}`, config);

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      let errorData;

      try {
        errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch {
        // If response is not JSON, use default error message
      }

      // Handle 401 Unauthorized - token might be expired
      if (response.status === 401) {
        console.warn("Received 401 Unauthorized, triggering logout");
        if (this.onTokenExpired) {
          this.onTokenExpired();
        }
        errorMessage = "Session expired. Please log in again.";
      }

      throw new APIError(errorMessage, response.status, errorData);
    }

    return response.json();
  }

  // Subscription endpoints
  async getSubscriptions() {
    return this.request<{ subscriptions: any[] }>("/subscriptions");
  }

  async cancelSubscription(subscriptionId: string | number) {
    return this.request<{ success: boolean; message: string }>(
      `/subscriptions/${subscriptionId}/cancel`,
      {
        method: "POST",
        body: JSON.stringify({}),
      }
    );
  }

  async reactivateSubscription(subscriptionId: string | number) {
    return this.request<{ success: boolean; message: string }>(
      `/subscriptions/${subscriptionId}/reactivate`,
      {
        method: "POST",
        body: JSON.stringify({}),
      }
    );
  }

  async pauseSubscription(subscriptionId: string | number, pauseData?: any) {
    return this.request<{ success: boolean; message: string }>(
      `/subscriptions/${subscriptionId}/pause`,
      {
        method: "POST",
        body: JSON.stringify(pauseData || {}),
      }
    );
  }

  async unpauseSubscription(subscriptionId: string | number) {
    return this.request<{ success: boolean; message: string }>(
      `/subscriptions/${subscriptionId}/unpause`,
      {
        method: "POST",
        body: JSON.stringify({}),
      }
    );
  }

  // Card endpoints
  async getCards() {
    return this.request<{ cards: any[] }>("/cards");
  }

  async addCard(cardData: any) {
    return this.request<{ success: boolean; card: any }>("/cards", {
      method: "POST",
      body: JSON.stringify(cardData),
    });
  }

  async updateCard(cardId: string | number, cardData: any) {
    return this.request<{ success: boolean; message: string; card: any }>("/cards", {
      method: "PUT",
      body: JSON.stringify({ cardId, ...cardData }),
    });
  }

  async removeCard(cardId: string | number) {
    return this.request<{ success: boolean; message: string }>(`/cards/${cardId}`, {
      method: "DELETE",
    });
  }
}

export const apiClient = new APIClient();
